import {
  NavigationProp,
  RouteProp,
  useNavigation,
  useRoute,
} from "@react-navigation/native";
import { format, startOfMonth } from "date-fns";
import { useMemo, useState } from "react";

import { InlineDatePicker } from "@whoosh/design-system/src/components/controls/Datepickers";
import { NumericalDayOfWeek } from "@whoosh/utilities/src/calendars";

import { ClubStackNavigatorParamList } from "../../../../../navigators/ClubNavigator";
import { useDateSelectorAvailabilityDateInfo } from "./useDateSelectorAvailabilityDateInfo";

type DateSelectorControlInnerProps = {
  facilityId: string;
  startDate: string;
  endDate: string;
  groupSize?: number;
  courseBucketIds?: readonly string[];
  startTime?: string;
  endTime?: string;
  minDuration: number;
  maxDuration: number;
  clubWeekStartsOn: NumericalDayOfWeek;
};

export const DateSelectorControlInner = ({
  facilityId,
  startDate,
  endDate,
  groupSize,
  courseBucketIds,
  startTime,
  endTime,
  minDuration,
  maxDuration,
  clubWeekStartsOn,
}: DateSelectorControlInnerProps) => {
  const navigation =
    useNavigation<NavigationProp<ClubStackNavigatorParamList, "Agenda">>();

  const { date: currentlySelectedDate } =
    useRoute<RouteProp<ClubStackNavigatorParamList, "Agenda">>().params;

  const [displayedMonthStartDate, setDisplayedMonthStartDate] = useState(
    format(startOfMonth(new Date()), "yyyy-MM-dd")
  );

  const dateInfo = useDateSelectorAvailabilityDateInfo({
    displayedMonthStartDate,
    facilityId,
    startDate,
    endDate,
    groupSize,
    courseBucketIds,
    startTime,
    endTime,
    minDuration,
    maxDuration,
  });

  const dateInfoWithSelection = useMemo(() => {
    const nextDateInfo = new Map(dateInfo);
    if (currentlySelectedDate) {
      const existingValue = nextDateInfo.get(currentlySelectedDate);
      nextDateInfo.set(currentlySelectedDate, {
        ...existingValue,
        selected: true,
      });
    }
    return nextDateInfo;
  }, [currentlySelectedDate, dateInfo]);

  const onDisplayedMonthChange = (monthStartDateString: string) => {
    setDisplayedMonthStartDate(monthStartDateString);
  };

  const onChange = (date: string) => {
    navigation.setParams({
      date,
    });
  };
  return (
    <InlineDatePicker
      startDate={startDate}
      endDate={endDate}
      onSelectDate={onChange}
      selectedDate={currentlySelectedDate}
      dateInfo={dateInfoWithSelection}
      fillEmptyDays
      showDayOfWeekHeader
      clubWeekStartsOn={clubWeekStartsOn}
      onDisplayedMonthChange={onDisplayedMonthChange}
    />
  );
};
